import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Globe, Info, Lock, Shield, Users } from "lucide-react";

import { resolveClubMediaUrl } from "../../features/comments/utils/clubFeedPostUtils.js";

function n(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
}

function pickMemberCount(club) {
  if (!club || typeof club !== "object") return null;
  return (
    n(club.member_count) ??
    n(club.members_count) ??
    n(club.members) ??
    n(club.memberCount) ??
    null
  );
}

function pickVisibilityLabel(club) {
  if (!club || typeof club !== "object") return null;
  const settings = club.settings && typeof club.settings === "object" ? club.settings : {};
  const raw =
    club.visibility ??
    settings.visibility ??
    club.join_policy ??
    settings.join_policy ??
    club.privacy ??
    null;
  if (raw == null || raw === "") return null;
  const s = String(raw).toLowerCase().replace(/_/g, " ");
  if (s.includes("private") || s === "invite") return "Private";
  if (s.includes("public")) return "Public";
  return String(raw).replace(/_/g, " ");
}

function pickRules(club) {
  if (!club || typeof club !== "object") return [];
  const settings = club.settings && typeof club.settings === "object" ? club.settings : {};
  if (Array.isArray(settings.rules) && settings.rules.length) {
    return settings.rules.filter(Boolean).map(String);
  }
  if (Array.isArray(club.rules) && club.rules.length) {
    return club.rules.filter(Boolean).map(String);
  }
  return [];
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * Right-column club context (Reddit-style community sidebar).
 */
export default function ClubThreadSidebar({
  club,
  slug,
  clubPublicPath,
  clubHomePath,
  accent = "emerald",
}) {
  const memberCount = pickMemberCount(club);
  const visibility = pickVisibilityLabel(club);
  const rules = pickRules(club);
  const description =
    club && typeof club.description === "string" && club.description.trim()
      ? club.description.trim()
      : null;
  const clubName = (club?.name && String(club.name).trim()) || (slug ? String(slug) : "Club");
  const thumb =
    resolveClubMediaUrl(club?.avatar_url ?? club?.logo_url ?? club?.icon_url ?? club?.image_url) ||
    resolveClubMediaUrl(club?.banner_url);

  const accentBtn =
    accent === "sky"
      ? "border-sky-400/25 bg-sky-500/10 text-sky-100 hover:bg-sky-500/15"
      : "border-emerald-400/25 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/15";

  return (
    <aside
      className={cn(
        "rounded-2xl border border-white/12 bg-gradient-to-b from-white/[0.07] to-white/[0.02] backdrop-blur-md shadow-[0_18px_50px_rgba(0,0,0,0.45)] overflow-hidden ring-1 ring-white/[0.06] w-full min-w-0 max-w-full box-border"
      )}
      aria-label="About this club"
    >
      <div className="p-4 sm:p-5 border-b border-white/10">
        <div className="flex gap-3 min-w-0">
          <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl border border-white/12 bg-black/30 overflow-hidden shrink-0 grid place-items-center text-lg font-bold text-white/90">
            {thumb ? (
              <img src={thumb} alt="" className="h-full w-full object-cover" />
            ) : (
              clubName.slice(0, 1).toUpperCase()
            )}
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 className="text-base sm:text-lg font-semibold text-white tracking-tight truncate">{clubName}</h2>
            {slug ? (
              <p className="mt-0.5 text-[11px] text-gray-500 font-mono truncate">/{slug}</p>
            ) : null}
          </div>
        </div>

        {description ? (
          <p className="mt-3 text-sm text-gray-400 leading-relaxed line-clamp-6">{description}</p>
        ) : club ? (
          <p className="mt-3 text-sm text-gray-500 italic">No description yet.</p>
        ) : (
          <p className="mt-3 text-sm text-gray-500 leading-relaxed">
            Club details could not be loaded. You can still use the links below.
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {visibility ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/25 px-2.5 py-1 text-[11px] font-medium text-gray-300">
              {String(visibility).toLowerCase().includes("private") ? (
                <Lock className="h-3.5 w-3.5 text-violet-300/90 shrink-0" />
              ) : (
                <Globe className="h-3.5 w-3.5 text-sky-300/90 shrink-0" />
              )}
              {visibility}
            </span>
          ) : null}
          {memberCount != null ? (
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/25 px-2.5 py-1 text-[11px] font-medium text-gray-300 tabular-nums">
              <Users className="h-3.5 w-3.5 text-gray-400 shrink-0" />
              {memberCount.toLocaleString()} members
            </span>
          ) : null}
        </div>
      </div>

      {rules.length > 0 ? (
        <div className="px-4 sm:px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            <Shield className="h-3.5 w-3.5" />
            Club rules
          </div>
          <ol className="mt-3 space-y-2 text-sm text-gray-400 list-decimal list-inside marker:text-gray-600">
            {rules.slice(0, 8).map((r, i) => (
              <li key={i} className="leading-relaxed pl-0.5">
                {r}
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      <div className="p-4 sm:p-5 space-y-2">
        <Link
          to={clubPublicPath}
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors",
            accentBtn
          )}
        >
          View club
          <ChevronRight className="h-4 w-4 opacity-80 shrink-0" />
        </Link>
        <Link
          to={clubHomePath}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.06] px-4 py-2.5 text-sm font-medium text-gray-200 hover:bg-white/[0.1] transition-colors"
        >
          <ArrowLeft className="h-4 w-4 opacity-70 shrink-0" />
          Club home & feed
        </Link>
        <p className="pt-1 flex items-start gap-2 text-[11px] text-gray-600 leading-relaxed">
          <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 opacity-70" />
          You’re viewing a single thread. The feed has more posts from this club.
        </p>
      </div>
    </aside>
  );
}
